import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Divider,
  Stack,
  SxProps,
  Paper,
  alpha,
  useTheme,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { useReportFilters } from "../hooks/useReportFilters";
import { FilterPopovers } from "./FilterPopovers";
import { FilterName, OfficerFilter } from "../types";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canReadUsers } from "../../../shared/auth/authorization";

interface ReportFiltersProps {
  filters: ReturnType<typeof useReportFilters>;
  availableReporters: OfficerFilter[];
  availableModifiers: OfficerFilter[];
  allReportStatuses: ReportStatus[];
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({
  filters,
  availableReporters,
  availableModifiers,
  allReportStatuses,
}) => {
  const theme = useTheme();
  const vars = theme;
  const permissions = usePermissions();
  const canReadUsersList = canReadUsers(permissions);
  // We manage the popover state here since it's driven by these buttons
  const [filterPopover, setFilterPopover] = useState<{
    name: FilterName | null;
    anchorEl: HTMLElement | null;
  }>({ name: null, anchorEl: null });

  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    name: FilterName
  ) => {
    setFilterPopover({
      name: filterPopover.name === name ? null : name,
      anchorEl: event.currentTarget,
    });
  };

  const handleClosePopover = () => {
    setFilterPopover({ name: null, anchorEl: null });
  };

  // Helper styles
  const filterButtonStyle = (isActive: boolean): SxProps => ({
    borderColor: isActive ? "transparent" : alpha(vars.palette.text.primary, 0.3),
    color: isActive ? "common.white" : "text.primary",
    backgroundColor: isActive ? "primary.dark" : "transparent",
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: isActive ? 700 : 500,
    fontSize: { xs: "12px", lg: "14px", xl: "15px" },
    // px: 1.5,
    minHeight: 44,
    "&:hover": {
      backgroundColor: isActive ? "primary.main" : "action.hover",
    },
  });

  // Calculate active states for buttons
  const activeFilters = {
    modifiedDate: Boolean(filters.dateFrom || filters.dateTo),
    reportedDate: Boolean(filters.reportedDateFrom || filters.reportedDateTo),
    reportedBy: filters.reportedByFilter.length > 0,
    modifiedBy: filters.modifiedByFilter.length > 0,
    status: filters.selectedStatuses.length > 0 && 
            filters.selectedStatuses.length < allReportStatuses.length,
  };

  const filterCounts = {
    reportedBy: filters.reportedByFilter.length,
    modifiedBy: filters.modifiedByFilter.length,
    reportedDate: activeFilters.reportedDate ? 1 : 0,
    modifiedDate: activeFilters.modifiedDate ? 1 : 0,
    status: activeFilters.status ? filters.selectedStatuses.length : 0,
  };

  const renderCountBadge = (count: number) =>
    count > 0 ? (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: "999px",
          backgroundColor: "background.paper",
          color: "primary.dark",
          fontWeight: 700,
          fontSize: "0.85rem",
          ml: 1,
        }}
      >
        {count}
      </Box>
    ) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        bgcolor: "background.paper",
        border: `1px solid ${alpha(vars.palette.text.primary, 0.15)}`,
        borderRadius: 2,
      }}
    >
      <Stack spacing={2.5}>
        <TextField
          placeholder="Search reports by details, keywords..."
          value={filters.detailsFilter}
          onChange={filters.handleDetailsFilterChange}
          fullWidth
          size="medium"
          sx={{ minHeight: 56, backgroundColor: "background.default" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Filters:
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            {/* Reported By */}
            {canReadUsersList && (
              <Button
                variant={activeFilters.reportedBy ? "contained" : "outlined"}
                size="medium"
                startIcon={<PersonIcon />}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{ color: activeFilters.reportedBy ? "common.white" : undefined }}
                  />
                }
                onClick={(e) => handleFilterClick(e, "reportedBy")}
                sx={filterButtonStyle(activeFilters.reportedBy)}
              >
                Reported By
                {renderCountBadge(filterCounts.reportedBy)}
              </Button>
            )}

            {/* Modified By */}
            {canReadUsersList && (
              <Button
                variant={activeFilters.modifiedBy ? "contained" : "outlined"}
                size="medium"
                startIcon={<PersonIcon />}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{ color: activeFilters.modifiedBy ? "common.white" : undefined }}
                  />
                }
                onClick={(e) => handleFilterClick(e, "modifiedBy")}
                sx={filterButtonStyle(activeFilters.modifiedBy)}
              >
                Modified By
                {renderCountBadge(filterCounts.modifiedBy)}
              </Button>
            )}

            {/* Reported Date */}
            <Button
              variant={activeFilters.reportedDate ? "contained" : "outlined"}
              size="medium"
              startIcon={<EventIcon />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{ color: activeFilters.reportedDate ? "common.white" : undefined }}
                />
              }
              onClick={(e) => handleFilterClick(e, "reportedDate")}
              sx={filterButtonStyle(activeFilters.reportedDate)}
            >
              Reported Date
              {renderCountBadge(filterCounts.reportedDate)}
            </Button>

            {/* Modified Date */}
            <Button
              variant={activeFilters.modifiedDate ? "contained" : "outlined"}
              size="medium"
              startIcon={<EventIcon />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{ color: activeFilters.modifiedDate ? "common.white" : undefined }}
                />
              }
              onClick={(e) => handleFilterClick(e, "modifiedDate")}
              sx={filterButtonStyle(activeFilters.modifiedDate)}
            >
              Modified Date
              {renderCountBadge(filterCounts.modifiedDate)}
            </Button>

            {/* Status */}
            <Button
              variant={activeFilters.status ? "contained" : "outlined"}
              size="medium"
              startIcon={<FilterListIcon />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{ color: activeFilters.status ? "common.white" : undefined }}
                />
              }
              onClick={(e) => handleFilterClick(e, "status")}
              sx={filterButtonStyle(activeFilters.status)}
            >
              Status
              {renderCountBadge(filterCounts.status)}
            </Button>
          </Stack>
        </Box>

        {filters.activeChips.length > 0 && (
          <>
            <Divider sx={{ borderColor: alpha(vars.palette.text.secondary, 0.2) }} />
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Button
                variant="text"
                color="inherit"
                startIcon={<CloseIcon fontSize="small" />}
                onClick={filters.handleResetFilters}
                sx={{
                  px: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Clear all ({filters.activeChips.length})
              </Button>

              {filters.activeChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  onDelete={chip.onDelete}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    backgroundColor: alpha(vars.palette.primary.dark, 0.08),
                  }}
                />
              ))}
            </Box>
          </>
        )}

        <FilterPopovers
          filterPopover={filterPopover}
          onClose={handleClosePopover}
          filters={filters}
          availableReporters={availableReporters}
          availableModifiers={availableModifiers}
          allReportStatuses={allReportStatuses}
        />
      </Stack>
    </Paper>
  );
};
